/**
 * Utility helper functions
 */

// In-memory CSRF token storage (more reliable than meta tag)
let csrfToken: string | null = null;

/**
 * Format date string to localized format
 *
 * @param dateString - ISO date string
 * @returns Formatted date or 'N/A'/'Invalid Date'
 *
 * @example
 * formatDate('2024-01-15T10:30:00Z') // '01/15/2024, 10:30 AM'
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid Date';
  }
}

/**
 * Redact sensitive data for safe logging
 * Recursively replaces values of sensitive keys with '[REDACTED]'
 *
 * @param data - Object to redact
 * @returns Copy with sensitive values redacted
 *
 * @example
 * redactSensitiveData({ user: 'john', password: 'secret' })
 * // { user: 'john', password: '[REDACTED]' }
 */
// Pre-compiled regex for O(1) sensitive key matching instead of O(M) array iteration
const SENSITIVE_KEY_PATTERN = /password|token|secret|authorization|cookie/i;

export function redactSensitiveData(data: unknown): unknown {
  if (typeof data !== 'object' || data === null) {
    return data;
  }

  const redacted = { ...data } as Record<string, unknown>;

  for (const key in redacted) {
    // Single regex test is O(K) where K is key length, vs O(M×K) for array iteration
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      redacted[key] = '[REDACTED]';
    } else if (typeof redacted[key] === 'object' && redacted[key] !== null) {
      redacted[key] = redactSensitiveData(redacted[key]);
    }
  }

  return redacted;
}

/**
 * Get CSRF token from in-memory storage or meta tag fallback
 *
 * @returns CSRF token or empty string
 */
export function getCsrfToken(): string {
  // First check in-memory token
  if (csrfToken) {
    return csrfToken;
  }
  // Fallback to meta tag for backward compatibility
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  return metaTag?.getAttribute('content') || '';
}

/**
 * Update CSRF token in memory and meta tag
 *
 * @param token - New CSRF token value
 */
export function updateCsrfToken(token: string): void {
  // Store in memory
  csrfToken = token;
  // Also update meta tag for backward compatibility
  let metaTag = document.querySelector('meta[name="csrf-token"]');
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute('name', 'csrf-token');
    document.head.appendChild(metaTag);
  }
  metaTag.setAttribute('content', token);
}

/**
 * Clear the CSRF token (call on logout)
 */
export function clearCsrfToken(): void {
  csrfToken = null;
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    metaTag.setAttribute('content', '');
  }
}
